import { Component, Input } from '@angular/core';
import { RequirementDto } from 'src/app/shared/models/job-unit-requirements.model';

import { UntilDestroy } from '@ngneat/until-destroy';
import { JobQualificationsChangeTypeEnum } from 'src/app/shared/components/job-qualifications/enums/job-qualifications-change-type.enum';
import { JobQualificationChange } from 'src/app/shared/components/job-qualifications/models/job-qualification-change.model';
import {
    AlternativeCertificationChange,
    TargetCertificationChange
} from 'src/app/shared/components/job-qualifications/models/alternative-certification-change.model';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { NgClass, NgTemplateOutlet } from '@angular/common';

@UntilDestroy()
@Component({
    selector: 'nova-job-qualifications-certs-change',
    templateUrl: './job-qualifications-certs-change.component.html',
    styleUrls: ['./job-qualifications-certs-change.component.scss'],
    standalone: true,
    imports: [NgClass, NgTemplateOutlet]
})
export class JobQualificationsCertsChangeComponent {
    @Input()
    certs: Array<JobQualificationChange<RequirementDto>>;

    @Input()
    altCerts: AlternativeCertificationChange[];

    changeType = JobQualificationsChangeTypeEnum;

    showAltCerts = this._ldFeatureManager.isEnabledSignal(FeatureFlag.AltCertsJobDetailsCompToggle);

    constructor(private readonly _ldFeatureManager: LDFeatureManager) {}

    getAlternativeCertsForSource(sourceCertId: number): AlternativeCertificationChange {
        if (!this.altCerts?.length) {
            return null;
        }
        return this.altCerts.find((ac) => ac.sourceCertificationId === sourceCertId);
    }

    hasAlternativeCerts(sourceCertId: number): boolean {
        const altCert = this.getAlternativeCertsForSource(sourceCertId);
        return (
            altCert && (altCert.originalTargetCertifications?.length > 0 || altCert.newTargetCertifications?.length > 0)
        );
    }

    getAltCertClass(targetCert: TargetCertificationChange): string {
        if (targetCert.changeType === JobQualificationsChangeTypeEnum.Added) {
            return 'job-qualifications-compare-change-added';
        }
        if (targetCert.changeType === JobQualificationsChangeTypeEnum.Removed) {
            return 'job-qualifications-compare-change-removed';
        }
        return '';
    }

    getAllTargetCerts(sourceCertId: number): TargetCertificationChange[] {
        const altCert = this.getAlternativeCertsForSource(sourceCertId);
        if (!altCert) {
            return [];
        }

        const allTargets = new Map<number, TargetCertificationChange>();

        altCert.originalTargetCertifications?.forEach((target) => {
            allTargets.set(target.id, target);
        });

        altCert.newTargetCertifications?.forEach((target) => {
            const existing = allTargets.get(target.id);
            if (!existing || target.changeType !== JobQualificationsChangeTypeEnum.Unchanged) {
                allTargets.set(target.id, target);
            }
        });

        return Array.from(allTargets.values());
    }
}
